/**
 * Simulation locale de l'assistant de recherche (add-on 24), sans fournisseur.
 * Les réponses citent des fiches de la galerie de démonstration ; la question
 * n'est jamais gardée.
 */
import {
  modificationReglagesRechercheSchema,
  questionRechercheSchema,
  REGLAGES_RECHERCHE_DEFAUT,
  retourRechercheSchema,
  type ReponseRecherche,
} from '@artosera/shared';
import { ApiError } from './errors.js';
import type { ReglagesRecherche } from './assistantRecherche.js';

let reglages: ReglagesRecherche = {
  ...REGLAGES_RECHERCHE_DEFAUT,
  dicteeWeb: true,
  version: 0,
  questionsDuMois: 42,
};
let compteur = 0;

function reponse(
  appelId: string,
  approfondir: boolean,
  corps: Omit<ReponseRecherche, 'appelId' | 'niveau'>,
): ReponseRecherche {
  return { ...corps, appelId, niveau: approfondir ? 'approfondi' : 'rapide' };
}

function repondre(question: string, approfondir: boolean): ReponseRecherche {
  compteur += 1;
  const appelId = `demo-recherche-${String(compteur)}`;
  const q = question.toLowerCase();
  if (/ajoute|crée|cree|modifie|supprime|add |create /.test(q))
    return reponse(appelId, approfondir, {
      reponse:
        'Je ne peux que chercher. Le carnet de contacts permet de créer ou de modifier une fiche.',
      sources: [],
      etapes: [],
      ecran: '/contacts',
    });
  if (/20\s?000|disponible|œuvre|oeuvre|artwork/.test(q))
    return reponse(appelId, approfondir, {
      reponse:
        'Deux œuvres disponibles sont proposées sous 20 000 € : « Courtesy » de Léa Bertrand à 8 200 € et « Étude pour un accrochage » de Hugo Massin à 1 800 €.',
      sources: [
        {
          type: 'oeuvre',
          id: 'oeuvre-2',
          libelle: 'Courtesy — Léa Bertrand',
          lien: '/oeuvres/oeuvre-2',
        },
        {
          type: 'oeuvre',
          id: 'oeuvre-4',
          libelle: 'Étude pour un accrochage — Hugo Massin',
          lien: '/oeuvres/oeuvre-4',
        },
      ],
      etapes: [{ outil: 'rechercher_oeuvres', etat: 'ok', nombre: 2 }],
    });
  if (/échéance|echeance|document|contrat|deadline/.test(q))
    return reponse(appelId, approfondir, {
      reponse:
        'Un contrat arrive à échéance : la représentation de Léa Bertrand, le 1 avril 2027. Un autre document confidentiel ne vous est pas ouvert.',
      sources: [
        {
          type: 'document',
          id: 'doc-1',
          libelle: 'Contrat de représentation — Léa Bertrand',
          lien: '/coffre/doc-1',
        },
      ],
      etapes: [{ outil: 'chercher_documents', etat: 'ok', nombre: 1 }],
      masques: 1,
    });
  if (/contact|demande|collectionneur|enquir/.test(q))
    return reponse(appelId, approfondir, {
      reponse: 'Étienne Lenoir est le contact collectionneur le plus récent de la galerie.',
      sources: [
        {
          type: 'contact',
          id: 'contact-1',
          libelle: 'Étienne Lenoir',
          lien: '/contacts/contact-1',
        },
      ],
      etapes: [
        { outil: 'rechercher_contacts', etat: 'ok', nombre: 1 },
        { outil: 'lister_factures', etat: 'refuse' },
      ],
    });
  return reponse(appelId, approfondir, {
    reponse:
      'Je n’ai rien trouvé pour cette recherche. Essayez avec le nom d’un artiste ou d’une œuvre.',
    sources: [],
    etapes: [{ outil: 'rechercher_oeuvres', etat: 'ok', nombre: 0 }],
  });
}

export function reponseDemoAssistantRecherche(
  chemin: string,
  methode: string,
  corps?: unknown,
): unknown {
  if (chemin === '/assistant-recherche/reglages' && methode === 'GET')
    return structuredClone(reglages);
  if (chemin === '/assistant-recherche/reglages' && methode === 'PATCH') {
    const { version, ...changement } = modificationReglagesRechercheSchema.parse(corps);
    if (version !== reglages.version)
      throw new ApiError(409, 'conflit.version', 'Réglages modifiés.');
    reglages = { ...reglages, ...changement, version: version + 1 };
    return structuredClone(reglages);
  }
  if (chemin === '/assistant-recherche/questions' && methode === 'POST') {
    const { question, approfondir } = questionRechercheSchema.parse(corps);
    reglages = { ...reglages, questionsDuMois: reglages.questionsDuMois + 1 };
    return repondre(question, approfondir === true);
  }
  if (/^\/assistant-recherche\/questions\/[^/]+\/retour$/.test(chemin) && methode === 'POST') {
    retourRechercheSchema.parse(corps);
    return {};
  }
  return undefined;
}
